import { useEffect, useState } from 'react';
import { API } from '@/App';

const NETWORK_ICON_LABEL = {
  TRC20: 'TRX',
  POLYGON: 'POL',
  BASE: 'BASE',
  ERC20: 'ETH',
};

/**
 * Landing-page accepted payment networks strip.
 * Mirrors the `/api/payments/methods` allowlist used by PaymentNetworkPicker
 * but renders as a passive, label-only luxury row — no fee captions, no selection.
 *
 * Layout: hairline rule · ACCEPTED PAYMENTS · hairline rule, then a row of
 * round monogram badges with the network name beneath.
 */
const AcceptedPaymentNetworks = () => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/payments/methods`);
        if (!r.ok) return;
        const d = await r.json();
        if (!alive) return;
        setMethods(d.methods || []);
      } catch (err) {
        console.warn('Accepted payments fetch failed:', err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading || methods.length === 0) return null;

  return (
    <section
      className="max-w-5xl mx-auto px-4 my-10 sm:my-14"
      data-testid="accepted-payment-networks"
      aria-label="Accepted payment networks"
    >
      {/* Eyebrow with flanking gold rules */}
      <div className="flex items-center justify-center gap-3 sm:gap-5 mb-6 sm:mb-7">
        <span className="h-[1px] w-16 sm:w-24 bg-gradient-to-r from-transparent to-[#cba135]/40" />
        <span
          className="uppercase text-[10px] sm:text-[11px] text-[#cba135]/70 whitespace-nowrap"
          style={{ fontFamily: "'Cinzel', serif", letterSpacing: '4px', fontWeight: 500 }}
        >
          Accepted Payments
        </span>
        <span className="h-[1px] w-16 sm:w-24 bg-gradient-to-l from-transparent to-[#cba135]/40" />
      </div>

      {/* Network row */}
      <ul className="flex flex-wrap items-start justify-center gap-x-8 gap-y-6 sm:gap-x-12">
        {methods.map((m) => {
          const monogram = NETWORK_ICON_LABEL[m.network] || m.network.slice(0, 3);
          return (
            <li
              key={m.network}
              className="flex flex-col items-center gap-2 select-none group"
              data-testid={`accepted-network-${m.network.toLowerCase()}`}
            >
              <div
                className="flex items-center justify-center h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-[#1a1508] border border-[#cba135]/25 text-[#cba135] text-[10px] sm:text-[11px] tracking-[0.04em] shadow-[inset_0_0_18px_rgba(203,161,53,0.06)] transition-all duration-300 group-hover:border-[#cba135]/60 group-hover:bg-[#cba135]/10 group-hover:shadow-[0_0_18px_rgba(203,161,53,0.18)]"
                style={{ fontFamily: "'Cinzel', serif", fontWeight: 600 }}
              >
                {monogram}
              </div>
              <span
                className="text-[10px] sm:text-[11px] uppercase text-[#cba135]/65 group-hover:text-[#cba135] transition-colors duration-200"
                style={{ fontFamily: "'Cinzel', serif", letterSpacing: '2px', fontWeight: 500 }}
              >
                {m.label.replace(/^USDT on /i, '')}
              </span>
            </li>
          );
        })}
      </ul>

      {/* USDT note */}
      <p className="text-center text-[9px] sm:text-[10px] uppercase tracking-[3px] text-[#cba135]/45 mt-6">
        USDT on every network · Secure via PayRam
      </p>
    </section>
  );
};

export default AcceptedPaymentNetworks;
